const STATUS_COLOR = {
  waiting:   '#9E9E9E',
  running:   '#09ff00',
  complete:  '#007bff',   
  completed: '#007bff',   
  'exclude from aggregation' :  "#ffc233",
  error:     '#EF5350',
  timeout:   '#FF7043',
  unknown:   '#9E9E9E',
};

const STATE_LABEL = {
  JOB_REQUESTED:  'Job Requested',
  JOB_READY:      'Job Ready',
  MODEL_DOWNLOAD: 'Model Download',
  TRAINING:       'Training',
  EVALUATION:     'Evaluation',
  TEST:           'Test',
  COMPLETE:       'Completed',
  AGG_OUT:        'Exclude from aggregation',
  TIME_OUT:       'Timeout',
  TIMEOUT:        'Timeout',
};

function normalizeStatus(jobStatus, hasEnd) {
  if (jobStatus != null) {
    const s = String(jobStatus).toLowerCase();
    if (s === 'complete') return 'completed';
    if (s === 'completed') return 'completed';
    if (s === 'agg_out') return 'exclude from aggregation';
    if (s === 'running') return 'running';
    if (s === 'waiting') return 'waiting';
    if (s === 'timeout' || s === 'time_out') return 'timeout';
    if (s === 'error' || s === 'failed' || s === 'failure') return 'error';
    return s;
  }
  return hasEnd ? 'completed' : 'running';
}

function latestByStart(timelines = []) {
  const withStart = timelines.filter(t => t?.startTime);
  if (withStart.length === 0) return undefined;
  return withStart.reduce((acc, cur) =>
    new Date(cur.startTime) > new Date(acc.startTime) ? cur : acc
  );
}

function msToDurationString(ms = 0) {
  if (!Number.isFinite(ms) || ms <= 0) return '00:00:00';
  const sTotal = Math.floor(ms / 1000);
  const days   = Math.floor(sTotal / 86400);
  const hours  = Math.floor((sTotal % 86400) / 3600);
  const mins   = Math.floor((sTotal % 3600) / 60);
  const secs   = sTotal % 60;
  const hhmmss = [hours, mins, secs].map(v => String(v).padStart(2, '0')).join(':');
  return days > 0 ? `${days}d ${hhmmss}` : hhmmss;
}

function sumDurations(list = [], startKey = 'startTime', endKey = 'endTime') {
  let total = 0;
  for (const it of list) {
    const s = it?.[startKey];
    const e = it?.[endKey];
    if (!s || !e) continue;
    const ms = new Date(e) - new Date(s);
    if (Number.isFinite(ms) && ms > 0) total += ms;
  }
  return total;
}

function rangeDurationMs(list = [], startKey = 'startTime', endKey = 'endTime') {
  const starts = list.map(r => r?.[startKey]).filter(Boolean).map(d => +new Date(d));
  const ends   = list.map(r => r?.[endKey]).filter(Boolean).map(d => +new Date(d));
  if (!starts.length || !ends.length) return 0;
  return Math.max(...ends) - Math.min(...starts);
}

function computeAggregationStartByRound(taskData = {}) {
  const devices = Array.isArray(taskData.deviceTimelines) ? taskData.deviceTimelines : [];
  const deviceCount = devices.length;
  const result = new Map(); 

  if (deviceCount === 0) return result;

  const acc = new Map();

  for (const block of devices) {
    const did = block?.deviceId;
    const list = Array.isArray(block?.timelines) ? block.timelines : [];
    const countedRounds = new Set();

    for (const it of list) {
      if (it?.jobState !== 'TRAINING') continue;
      const rn = it?.roundNum;
      if (rn == null || countedRounds.has(rn)) continue;

      const hasEnd = !!it?.endTime;
      if (!acc.has(rn)) acc.set(rn, { completedCount: 0, latestEnd: null, seen: new Set() });
      const slot = acc.get(rn);

      if (did && slot.seen.has(did)) continue;
      slot.seen.add(did);

      if (hasEnd) {
        slot.completedCount += 1;
        const t = new Date(it.endTime);
        if (!slot.latestEnd || t > slot.latestEnd) slot.latestEnd = t;
      }
      countedRounds.add(rn);
    }
  }

  for (const [rn, { completedCount, latestEnd }] of acc.entries()) {
    if (completedCount === deviceCount && latestEnd) {
      result.set(rn, latestEnd.toISOString());
    }
  }
  return result;
}

function makeServerEntry(taskData = {}) {
  const rounds = Array.isArray(taskData.roundTimelines) ? taskData.roundTimelines : [];
  const maxRound = Number(taskData.maxRound ?? 0);
  const deviceCount = Array.isArray(taskData.deviceTimelines) ? taskData.deviceTimelines.length : 0;
  const currentRound =
    Number(
      taskData.currentRound ??
      (rounds.length ? Math.max(...rounds.map(r => Number(r.roundNum ?? 0))) : 0)
    );

  const status = rounds.length === maxRound ? 'complete' : 'running';
  const state  = status === 'complete' ? 'complete' : 'active';

  const aggStartByRound = computeAggregationStartByRound(taskData);

  const tasks = rounds
    .slice()
    .sort((a, b) => new Date(a.startTime ?? 0) - new Date(b.startTime ?? 0))
    .flatMap(r => {
      const rn = r.roundNum;
      const roundStart = r?.startTime ?? null;
      const roundEnd   = r?.endTime   ?? null;
      const aggStart   = aggStartByRound.get(rn); 

      const trainingDone = !!aggStart;
      const trainingTask = {
        task:     `${rn} Round Training`,
        roundNum: rn, 
        status:   trainingDone ? 'completed' : 'running',
        color:    STATUS_COLOR[trainingDone ? 'completed' : 'running'],
        start:    roundStart,
        end:      trainingDone ? aggStart : null,
      };

      if (!trainingDone) return [trainingTask];

      const aggCompleted = !!roundEnd;
      const aggregationTask = {
        task:     `${rn} Round Aggregation`,
        roundNum: rn, 
        status:   aggCompleted ? 'completed' : 'running',
        color:    STATUS_COLOR[aggCompleted ? 'completed' : 'running'],
        start:    aggStart,
        end:      roundEnd ?? null,
      };

      return [trainingTask, aggregationTask];
    });

  const totalSpanMs = rangeDurationMs(rounds, 'startTime', 'endTime');
  const summary = `${currentRound} Round • ${msToDurationString(totalSpanMs)}`;

  return { name: 'server', status, state, tasks, summary };
}

function ensureServerFirst(arr = []) {
  const idx = arr.findIndex(x => x?.name === 'server');
  if (idx > 0) {
    const [server] = arr.splice(idx, 1);
    arr.unshift(server);
  }
  return arr;
}

function buildDeviceSummary(list = []) {
  const roundSet = new Set();

  const roundTimeout = new Map(); 
  for (const it of list) {
    const rn = it?.roundNum;
    if (rn == null) continue;

    roundSet.add(rn);

    const status = normalizeStatus(it?.jobStatus, !!it?.endTime);
    let isTimeout = status === 'timeout';

    if (!isTimeout) {
      const stateStr = String(it?.jobState ?? '').toUpperCase();
      if (stateStr.includes('TIMEOUT') || stateStr.includes('TIME_OUT')) {
        isTimeout = true;
      }
    }

    if (!roundTimeout.has(rn)) roundTimeout.set(rn, false);
    if (isTimeout) roundTimeout.set(rn, true);
  }

  const timeoutRounds = Array.from(roundTimeout.values()).filter(Boolean).length;

  let total = 0;
  for (const it of list) {
    const s = it?.startTime;
    const e = it?.endTime;
    if (!s || !e) continue;

    const stateStr = String(it?.jobState ?? '').toUpperCase();
    if (stateStr === 'JOB_READY') continue; 

    const ms = new Date(e) - new Date(s);
    if (Number.isFinite(ms) && ms > 0) total += ms;
  }

  return `${roundSet.size} Rounds • ${msToDurationString(total)}`;
}

export default function preprocessing(taskData) {
  const result = [];

  result.push(makeServerEntry(taskData));

  const deviceBlocks = Array.isArray(taskData?.deviceTimelines)
    ? taskData.deviceTimelines
    : [];

  for (const block of deviceBlocks) {
    const deviceId = block?.deviceId;
    const list = Array.isArray(block?.timelines) ? block.timelines : [];
    if (!deviceId || list.length === 0) continue;

    const latest = latestByStart(list) || {};
    const latestStatus = normalizeStatus(latest.jobStatus, !!latest.endTime);
    const latestState  = latest.jobState;

    const tasks = list
      .filter(it => it?.startTime)
      .slice()
      .sort((a, b) => new Date(a.startTime) - new Date(b.startTime))
      .map(it => {
        const status = normalizeStatus(it.jobStatus, !!it.endTime);
        const color  = STATUS_COLOR[status] ?? STATUS_COLOR.unknown;
        const label  = STATE_LABEL[it.jobState] ?? it.jobState ?? 'Unknown';
        return {
          task:     `${it.roundNum}R ${label ?? ''}`,
          roundNum: it.roundNum ?? null,
          status,
          color,
          start:    it.startTime ?? null,
          end:      it.endTime ?? null,
        };
      });

    const summary = buildDeviceSummary(list);

    result.push({
      name: deviceId,
      status: latestStatus,
      state: latestState,
      tasks,
      summary,
    });
  }

  return ensureServerFirst(result);
}
